import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import type { UsageInfo } from "./useDashboardData";

const API_BASE = "https://wpp.debugin.com/ai";

export interface HistorySnapshot {
  date: string;
  messages: number;
  tokens: number;
  inputTokens: number;
  outputTokens: number;
}

const STORAGE_KEY_PREFIX = "usage_history_";

function getTodayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

function loadHistory(session: string): HistorySnapshot[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${session}`);
    if (!raw) return [];
    return JSON.parse(raw) as HistorySnapshot[];
  } catch {
    return [];
  }
}

function saveHistory(session: string, history: HistorySnapshot[]): void {
  const trimmed = history.slice(-90);
  localStorage.setItem(`${STORAGE_KEY_PREFIX}${session}`, JSON.stringify(trimmed));
}

export interface UsageHistoryData {
  history: HistorySnapshot[];
  today: HistorySnapshot | null;
  hasHistory: boolean;
  recordSnapshot: (usage: UsageInfo) => void;
}

export function useUsageHistory(): UsageHistoryData {
  const { user } = useAuth();
  const session = user?.session ?? "";
  const [history, setHistory] = useState<HistorySnapshot[]>([]);
  const recordedRef = useRef(false);

  useEffect(() => {
    if (!session) return;
    setHistory(loadHistory(session));
    recordedRef.current = false;
  }, [session]);

  const recordSnapshot = useCallback(
    (usage: UsageInfo) => {
      if (!session || recordedRef.current) return;
      const todayKey = getTodayKey();
      const current = loadHistory(session);
      const exists = current.some((s) => s.date === todayKey);
      if (exists) {
        recordedRef.current = true;
        setHistory(current);
        return;
      }
      const snapshot: HistorySnapshot = {
        date: todayKey,
        messages: usage.messages ?? 0,
        tokens: usage.tokens ?? 0,
        inputTokens: usage.input ?? 0,
        outputTokens: usage.output ?? 0,
      };
      const updated = [...current, snapshot];
      saveHistory(session, updated);
      setHistory(updated);
      recordedRef.current = true;
    },
    [session],
  );

  const today = history.length > 0 ? history[history.length - 1] : null;

  return {
    history: history.map((h) => ({ ...h, date: formatDateLabel(h.date) })),
    today,
    hasHistory: history.length > 0,
    recordSnapshot,
  };
}